import React from "react";
import { Search, X } from "lucide-react";
import type { Node } from "reactflow";
import type { NodeData } from "../../lib/api";

type SidebarProps = {
  nodes: Node<NodeData>[];
  typeCounts: Record<string, number>;
  getTypeLabel: (type: string) => string;
  searchTerm: string;
  normalizedSearch: string;
  onSearchChange: (value: string) => void;
  searchInputRef: React.RefObject<HTMLInputElement>;
  matchesSearch: (node: NodeData) => boolean;
  contactsForSearch: (node: NodeData) => any[];
  onSelectNode: (id: string) => void;
  hideInactive: boolean;
  onToggleHideInactive: (value: boolean) => void;
  onOpenContacts: () => void;
  onNavigateStructures: () => void;
  viewOnlyMode: boolean;
  onClose: () => void;
};

export function Sidebar({
  nodes,
  typeCounts,
  getTypeLabel,
  searchTerm,
  normalizedSearch,
  onSearchChange,
  searchInputRef,
  matchesSearch,
  contactsForSearch,
  onSelectNode,
  hideInactive,
  onToggleHideInactive,
  onOpenContacts,
  onNavigateStructures,
  viewOnlyMode,
  onClose,
}: SidebarProps) {
  const results =
    searchTerm && nodes.length > 0
      ? nodes.filter((n) => matchesSearch(n.data as NodeData)).slice(0, 10)
      : [];

  return (
    <aside className="fixed md:relative inset-0 md:inset-auto w-full md:w-72 border-r border-[var(--color-border)] p-4 space-y-4 bg-[var(--color-panel)] md:bg-[var(--color-panel)]/85 backdrop-blur z-20 overflow-y-auto">
      <div className="flex items-center justify-between">
        <div>
          <span className="text-sm font-semibold">Kategorien</span>
          <div className="text-xs text-[var(--color-muted)]">Filter &amp; Quick Actions</div>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-1 rounded-full bg-[var(--color-panel-2)] text-xs text-[var(--color-muted)]">
            {nodes.length} Nodes
          </span>
          <button
            onClick={onClose}
            className="p-1 hover:bg-[var(--color-panel-2)] rounded transition-colors"
            title="Sidebar schließen"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="space-y-2">
        {Object.entries(typeCounts).map(([type, count]) => (
          <div
            key={type}
            className="flex items-center justify-between text-sm rounded-lg border border-[var(--color-border)] px-3 py-2 bg-white/70 dark:bg-slate-900/40"
          >
            <span className="capitalize">{getTypeLabel(type)}</span>
            <span className="px-2 py-1 rounded-full bg-[var(--color-panel-2)] text-xs">{count}</span>
          </div>
        ))}
        {Object.keys(typeCounts).length === 0 && <div className="text-xs text-[var(--color-muted)]">Noch keine Nodes.</div>}
      </div>
      <div className="space-y-2 rounded-lg border border-[var(--color-border)] bg-[var(--color-panel-2)]/80 p-3">
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-3 text-[var(--color-muted)]" />
          <input
            placeholder="Suche nach Node oder Kontakt..."
            className="pl-9 pr-3 py-2 rounded-md border border-[var(--color-border)] bg-[var(--color-panel)] focus:ring-2 focus:ring-[var(--color-accent)] outline-none w-full"
            value={searchTerm}
            onChange={(e) => onSearchChange(e.target.value)}
            ref={searchInputRef}
          />
        </div>
        {searchTerm && nodes.length > 0 && (
          <div className="max-h-64 overflow-y-auto space-y-1">
            <div className="text-xs text-[var(--color-muted)] mb-1 px-1">Suchergebnisse:</div>
            {results.map((n) => {
              const data = n.data as NodeData;
              const contacts = contactsForSearch(data);
              const contactHit = normalizedSearch
                ? contacts.find((c) => {
                    const values = [c.name, c.role, c.email, c.phone, c.signal, c.matrix, c.telegram, c.website];
                    return values.some((v) => v && v.toString().toLowerCase().includes(normalizedSearch));
                  })
                : null;
              return (
                <button
                  key={n.id}
                  onClick={() => onSelectNode(n.id)}
                  className="w-full text-left px-3 py-2 rounded-md hover:bg-[var(--color-panel)] border border-transparent hover:border-[var(--color-accent)] transition-colors text-sm"
                >
                  <div className="font-medium truncate">{data.label}</div>
                  {data.type && (
                    <div className="text-xs text-[var(--color-muted)] capitalize">{data.type}</div>
                  )}
                  {contactHit && (
                    <div className="text-[11px] text-[var(--color-muted)] truncate">
                      Kontakt: {contactHit.name}
                      {contactHit.role ? ` · ${contactHit.role}` : ""}
                    </div>
                  )}
                </button>
              );
            })}
            {results.length === 0 && (
              <div className="text-xs text-[var(--color-muted)] px-3 py-2">Keine Ergebnisse gefunden</div>
            )}
          </div>
        )}
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={hideInactive} onChange={(e) => onToggleHideInactive(e.target.checked)} />
          Inaktive ausblenden
        </label>
        <button className="btn btn-secondary w-full justify-center" onClick={onOpenContacts}>
          Kontakte
        </button>
        <button className="btn btn-secondary w-full justify-center" onClick={onNavigateStructures}>
          Strukturen
        </button>
        {!viewOnlyMode && (
          <div className="text-xs text-[var(--color-muted)]">
            Weitere Aktionen findest du in den Einstellungen.
          </div>
        )}
      </div>
    </aside>
  );
}
